import React, { useCallback, useContext, useState } from 'react';
import cn from 'classnames';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { CommentContext } from '../../CommentContext';
import SimpleHoverPopperPopover from '@wix/da-ds/pkg/popper/presets/SimpleHoverPopperPopover';
import SimplePopperPopover from '@wix/da-ds/pkg/popper/presets/SimplePopperPopover';
import ExclusiveAvatar, {
  Props as ExclusiveAvatarProps,
} from './ExclusiveAvatar';
import ExclusiveAvatarOverlay from '../../../CommentBox/lego/ExclusiveAvatarOverlay';
import { getDeviationUrl } from '../../../DeviationViews/_helpers';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  BiData,
  BiContextProvidedValue,
  ItemClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ResourceType } from '../../../types/resource';
import { useExtendedDeviation } from '../../../utils/hooks/useExtendedDeviation';
import { PapiDeviation } from '@wix/da-papi-types';

import s from './ExclusiveAvatarWithPopover.scss';

const HOVER_DELAY = 500;

export interface Props extends ExclusiveAvatarProps {
  /** Whether to show the popover on hover */
  withPopover?: boolean;
  /** Optional click handler to override default behavior */
  onAvatarClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Optional aria-label to improve a11y for the clickable avatar */
  avatarAriaLabel?: string;
}

/**
 * ExclusiveAvatar with hover popover that shows ExclusiveAvatarOverlay
 */
const ExclusiveAvatarWithPopover: React.FC<Props> = ({
  withPopover = true,
  deviation,
  className,
  onAvatarClick,
  avatarAriaLabel,
  ...props
}) => {
  const isMobile = useContext(MobileContext);
  const { comment } = useContext(CommentContext);
  const logBiEvent = useBiLogger();

  const shouldShowPopoverOnDesktop = withPopover && !isMobile;

  const defaultNavigate = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!deviation) return;
      const url = getDeviationUrl(deviation as any);
      if (url) {
        window.location.assign(url);
      }
    },
    [deviation]
  );

  const [isMobilePopoverVisible, setIsMobilePopoverVisible] = useState(false);
  const [extendedDeviation, setExtendedDeviation] = useState<
    PapiDeviation | undefined
  >(undefined);
  const [shouldInitExtended, setShouldInitExtended] = useState(false);
  const hoverTimerRef = React.useRef<number | undefined>(undefined);

  // Reset extended deviation when the base deviation changes
  React.useEffect(() => {
    setExtendedDeviation(undefined);
    setShouldInitExtended(false);
    if (hoverTimerRef.current) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = undefined;
    }
  }, [deviation?.deviationId]);

  // Helper component to trigger fetching of extended deviation only after threshold
  const ExtendedDeviationLoader: React.FC<{
    onLoaded: (d: PapiDeviation) => void;
  }> = ({ onLoaded }) => {
    const result = useExtendedDeviation(deviation);
    React.useEffect(() => {
      if (result) {
        onLoaded(result as PapiDeviation);
      }
    }, [result, onLoaded]);
    return null;
  };

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Log BI event for exclusive avatar click
      if (deviation) {
        const isForSale = deviation.adoptable?.status === 'onsale';
        const fixedPrice = deviation.adoptable?.fixedPrice?.fixedPrice || 0;
        const baseSectionName = 'exclusive_avatar_popup';

        logBiEvent(
          BiData<ItemClickBiEvent>({
            evid: 100,
            sectionname:
              isForSale && fixedPrice
                ? `${baseSectionName}_purchase`
                : baseSectionName,
            page_typeid: BiContextProvidedValue,
            page_itemid: BiContextProvidedValue,
            typeid: ResourceType.DEVIATION,
            itemid: deviation.deviationId,
          })
        );
      }

      if (onAvatarClick) {
        onAvatarClick(e);
        return;
      }
      if (withPopover && isMobile) {
        e.preventDefault();
        e.stopPropagation();
        setIsMobilePopoverVisible(v => !v);
        return;
      }
      // Desktop default: navigate to deviation
      defaultNavigate(e);
    },
    [
      onAvatarClick,
      withPopover,
      isMobile,
      defaultNavigate,
      deviation,
      logBiEvent,
    ]
  );

  if (!shouldShowPopoverOnDesktop && (!withPopover || isMobile)) {
    // Either popover disabled or on mobile (click opens popover if no custom handler)
    if (withPopover && isMobile) {
      const PopoverContent = () => {
        return (
          <div className={s['popover-wrapper']}>
            <ExclusiveAvatarOverlay
              deviation={deviation}
              commentedByUser={comment?.user}
            />
          </div>
        );
      };

      return (
        <SimplePopperPopover
          className={cn(s['popover-root'], className)}
          contentClassName={s['popover-content']}
          isVisible={isMobilePopoverVisible}
          onClickOutside={() => setIsMobilePopoverVisible(false)}
          showArrow={false}
          usePortal
          popoverContent={<PopoverContent />}
        >
          <button
            type="button"
            className={s['avatar-button']}
            onClick={handleClick}
            onMouseEnter={() => {
              if (hoverTimerRef.current) {
                window.clearTimeout(hoverTimerRef.current);
              }
              hoverTimerRef.current = window.setTimeout(() => {
                setShouldInitExtended(true);
              }, HOVER_DELAY);
            }}
            onMouseLeave={() => {
              if (hoverTimerRef.current) {
                window.clearTimeout(hoverTimerRef.current);
                hoverTimerRef.current = undefined;
              }
            }}
            aria-label={avatarAriaLabel}
          >
            {shouldInitExtended && !extendedDeviation && (
              <ExtendedDeviationLoader onLoaded={setExtendedDeviation} />
            )}
            <ExclusiveAvatar
              deviation={(extendedDeviation as any) || deviation}
              {...props}
            />
          </button>
        </SimplePopperPopover>
      );
    }

    return (
      <button
        type="button"
        className={cn(s['avatar-button'], className)}
        onClick={handleClick}
        onMouseEnter={() => {
          if (hoverTimerRef.current) {
            window.clearTimeout(hoverTimerRef.current);
          }
          hoverTimerRef.current = window.setTimeout(() => {
            setShouldInitExtended(true);
          }, HOVER_DELAY);
        }}
        onMouseLeave={() => {
          if (hoverTimerRef.current) {
            window.clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = undefined;
          }
        }}
        aria-label={avatarAriaLabel}
      >
        {shouldInitExtended && !extendedDeviation && (
          <ExtendedDeviationLoader onLoaded={setExtendedDeviation} />
        )}
        <ExclusiveAvatar deviation={deviation} {...props} />
      </button>
    );
  }

  const PopoverContent = () => {
    return (
      <div className={s['popover-wrapper']}>
        <ExclusiveAvatarOverlay
          deviation={extendedDeviation || deviation}
          commentedByUser={comment?.user}
        />
      </div>
    );
  };

  return (
    <SimpleHoverPopperPopover
      className={cn(s['popover-root'], className)}
      contentClassName={s['popover-content']}
      popoverContent={<PopoverContent />}
      noPadding={true}
      hoverDelay={HOVER_DELAY}
      showArrow={false}
    >
      <button
        type="button"
        className={s['avatar-button']}
        onClick={handleClick}
        onMouseEnter={() => {
          if (hoverTimerRef.current) {
            window.clearTimeout(hoverTimerRef.current);
          }
          hoverTimerRef.current = window.setTimeout(() => {
            setShouldInitExtended(true);
          }, HOVER_DELAY);
        }}
        onMouseLeave={() => {
          if (hoverTimerRef.current) {
            window.clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = undefined;
          }
        }}
        aria-label={avatarAriaLabel}
      >
        {shouldInitExtended && !extendedDeviation && (
          <ExtendedDeviationLoader onLoaded={setExtendedDeviation} />
        )}
        <ExclusiveAvatar
          deviation={(extendedDeviation as any) || deviation}
          {...props}
        />
      </button>
    </SimpleHoverPopperPopover>
  );
};

ExclusiveAvatarWithPopover.displayName = 'ExclusiveAvatarWithPopover';

export default ExclusiveAvatarWithPopover;
